// `@woo/api/extension/extension-job.api.ng1.ts` — the ATS jobs an automated tenant searches
// alongside its manual alerts (plan §7 phase 3, slice 2).
import { request } from './request.ts'
import type { ExtensionAlertContainer } from './alerts.ts'

/** `IExtensionJobDTO`, narrowed to what the dropdown's job rows render. */
export interface ExtensionJob {
  id: number
  /** The ATS's own id, shown under the title. */
  externalJobId: string
  title: string
  employerName: string
}

/** `IExtensionJobSearchResponseDTO`. `totalResultCount` is the denominator in the group count. */
export interface ExtensionJobSearchResponse {
  query: string
  results: ExtensionJob[]
  firstResult: number
  maxResults: number
  totalResultCount: number
}

/**
 * `extension-job/search`.
 *
 * ⚠️ `exclude[]` IS ONE COMMA-JOINED PARAMETER, NOT REPEATED KEYS. Upstream sends
 * `{ 'exclude[]': exclude.join(',') }` (extension-job.api.ng1.ts:67), so two excluded ids go
 * as `exclude[]=1,2` and not `exclude[]=1&exclude[]=2`. The bracket in the name makes the
 * repeated form look right, which is how it would get "tidied" into something the server reads
 * as a single id of "1".
 *
 * ⚠️ AND IT IS SENT EVEN WHEN EMPTY. The observed call from the live app is
 * `search?exclude%5B%5D=` — upstream passes `{ 'exclude[]': '' }` whenever `exclude` is an
 * array at all, including the empty one it builds when the current alert has no job. Omitting
 * it for the empty case would be a different request from the one production makes.
 *
 * `q`, `firstResult` and `maxResults` are omitted when undefined, which is what `$http` does
 * with undefined params.
 */
export async function searchExtensionJobs(options: {
  query?: string
  exclude?: number[]
  firstResult?: number
  maxResults?: number
}): Promise<ExtensionJobSearchResponse> {
  const params = new URLSearchParams()
  if (options.query !== undefined && options.query.length > 0) params.set('q', options.query)
  if (options.firstResult !== undefined) params.set('firstResult', String(options.firstResult))
  if (options.maxResults !== undefined) params.set('maxResults', String(options.maxResults))
  if (options.exclude !== undefined) params.set('exclude[]', options.exclude.join(','))
  return request<ExtensionJobSearchResponse>('GET', `/chrome-extension-api/extension-job/search?${params.toString()}`)
}

/**
 * `convertJobToAlert` — what the alert-list panel's "Job searches" rows resolve through.
 *
 * ⚠️ IT CREATES AN ALERT WHEN THE JOB HAS NONE, which is why the caller must look first.
 * `redirect-job-to-alert.resolver.ng1.ts:43` searches the alerts already loaded for one whose
 * `extensionJobId` matches and only calls this when there is no match — so the common click is
 * a navigation and the first click on a never-opened job is a write. Calling it
 * unconditionally would create a duplicate alert on every visit.
 *
 * ⚠️ A POST WITH THE ID IN THE QUERY AND AN EMPTY OBJECT AS THE BODY. Upstream sends
 * `params: { extensionJobId }, data: {}`; the body is not optional — it is what makes the
 * request `application/json`.
 */
export async function convertJobToAlert(extensionJobId: number): Promise<ExtensionAlertContainer> {
  return request(
    'POST',
    `/chrome-extension-api/extension-job/convertJobToAlert?${new URLSearchParams({ extensionJobId: String(extensionJobId) }).toString()}`,
    {},
  )
}

// ── Adding a candidate to an ATS job (`extension-alert-candidates-add-to-job`) ──
//
// ⚠️ THREE ENDPOINTS ON TWO DIFFERENT CONTROLLERS, and the panel needs all three. The job
// search is `job-search/searchJobsByTitle`, NOT the `extension-job/search` above — that one
// backs the alert dropdown's job groups and answers a different shape.

/** `IExtensionJobSearchDto` — what the autocomplete list and the default values return. */
export interface AtsJobSearchResult {
  id: number
  title: string
  /** The ATS's own job code; shown only when `careero.atsHasJobCode`. */
  internalTitle?: string | null
  /** Shown only when `careero.atsHasConcreteEmployers`. */
  employerName?: string | null
}

/** `IExtensionRawJobStatusItem` — `status` is submitted, `statusText` is displayed. */
export interface AtsJobStatusItem {
  status: string
  statusText: string
}

/** `IAddToAtsJobDefaultValues` — what the form opens with, per alert. */
export interface AddToAtsJobDefaults {
  job?: AtsJobSearchResult | null
  status?: string | null
}

/** `AddToAtsJobSource` — which entry point asked, which changes the defaults the server picks. */
export const AddToAtsJobSource = {
  Dedicated: 'DEDICATED',
  Message: 'MESSAGE',
  AddToAtsAutomation: 'ADD_TO_ATS_AUTOMATION',
} as const
export type AddToAtsJobSource = (typeof AddToAtsJobSource)[keyof typeof AddToAtsJobSource]

/** `typeaheads.getExtensionAtsJobTitles` — the job field's autocomplete. */
export async function searchAtsJobsByTitle(query: string): Promise<AtsJobSearchResult[]> {
  return request('GET', `/chrome-extension-api/job-search/searchJobsByTitle?q=${encodeURIComponent(query)}`)
}

/** `getAllJobStatusItems` — every status the ATS offers, for the Status dropdown. */
export async function getAllJobStatusItems(): Promise<AtsJobStatusItem[]> {
  return request('GET', '/chrome-extension-api/job-search/getAllJobStatusItems')
}

/** `getAddToJobDefaultValues` — the job and status the form should open on. */
export async function getAddToJobDefaultValues(options: {
  alertId?: number
  source: AddToAtsJobSource
}): Promise<AddToAtsJobDefaults> {
  const params = new URLSearchParams({ source: options.source })
  if (options.alertId !== undefined) params.set('alertId', String(options.alertId))
  return request('GET', `/chrome-extension-api/add-to-ats-job/getAddToJobDefaultValues?${params.toString()}`)
}

/**
 * `addToAtsJobAsync` — enqueues the write and returns; it does not wait on the ATS.
 *
 * ⚠️ THIS WRITES INTO A CUSTOMER'S ATS. It is deduped and idempotent server-side, and upstream
 * awaits it with a bounded timeout so that a network failure still counts as queued — but it is
 * an outward-facing write all the same, and nothing should call it except a user pressing Add.
 */
export async function addToAtsJobAsync(request_: {
  jobId: number
  status: string
  candidateIds: number[]
  alertId?: number
}): Promise<{ isSuccessful: boolean }> {
  // `addToAtsJobAsync(request, true)` from the add-to-job panel, which reports its own failure.
  return request('POST', '/chrome-extension-api/add-to-ats-job/addToAtsJobAsync', request_, {}, { suppressErrorDialog: true })
}
